// Money → Transactions (spec 044 D50–D59, plan slice 7): how a ledger row looks — the
// pill, the title, the CSV export. Pure — no React, no Firestore — the page maps `look`
// to its own Remix icon.
import { money, egp, dayTimeLabel, displayName } from "./queuePresentation";
import { sessionsLabel, txnRow } from "./studentDetail";
import { toDate } from "./ticketPresentation";
import { kindOf, periodEnd } from "./transactionLedger";

export const LOOKS = {
  topup: { pill: "Top-up", tone: "success" },
  checkout: { pill: "Checkout", tone: "default" },
  lesson: { pill: "Lesson", tone: "info" },
  refund: { pill: "Refund", tone: "warning" },
  adjustment: { pill: "Adjustment", tone: "default" },
  referral: { pill: "Referral", tone: "success" },
  other: { pill: "Other", tone: "default" },
  tutorPay: { pill: "Tutor pay", tone: "default" },
};

// R13: a lesson-kind row looks like a lesson or a refund by its `type`; every other kind
// is its own look.
export const rowLook = (t = {}) => {
  const kind = kindOf(t);
  if (kind === "lesson") return t.type === "refund" ? "refund" : "lesson";
  return kind;
};

// R15: an id with no user document reads "Unknown account" over its first 8 characters.
// When the name *is* the email (displayName's fallback), the line under it is empty.
export const personOf = (uid, people = {}) => {
  const u = uid && people[uid];
  if (!u) return { name: "Unknown account", sub: uid ? String(uid).slice(0, 8) : "" };
  const name = displayName(u, "Unknown account");
  return { name, sub: name === u.email ? "" : u.email || "" };
};

const MILESTONE = { topup: "first top-up", lesson: "first lesson" };

const titleFor = (look, t) => {
  switch (look) {
    case "topup":
      return t.hours ? `Top-up · ${sessionsLabel(t.hours)}` : "Top-up";
    case "checkout":
      return "Checkout started";
    case "lesson":
      return t.transactionType === "lesson" ? "Lesson booked" : "Lesson";
    case "refund":
      return t.transactionType === "cancelledwithrefund" ? "Refund · lesson cancelled" : "Refund";
    case "adjustment":
      return t.reason ? `Adjustment · ${t.reason}` : "Adjustment";
    case "referral": {
      const milestone = String(t.reason || "").split(":")[1];
      return MILESTONE[milestone] ? `Referral reward · ${MILESTONE[milestone]}` : "Referral reward";
    }
    case "tutorPay":
      return "Tutor paid for a late cancel";
    default: {
      const OTHER = {
        trial: "Free session booked",
        trialReturned: "Free session returned",
        cancelledwithoutrefund: "Late cancel · no refund",
        cancelledwithoutrefunddonation: "Late cancel · donated",
        cancelledwithrefund: "Refund · lesson cancelled",
      };
      return OTHER[t.transactionType] || txnRow(t).type;
    }
  }
};

const subFor = (look, t, people) => {
  switch (look) {
    case "topup":
    case "checkout":
      return t.paymentIntentId || "";
    case "adjustment":
      return t.adjustedBy ? `by ${personOf(t.adjustedBy, people).name}` : "";
    case "referral": {
      const friendUid = String(t.reason || "").split(":")[2];
      return friendUid ? `Friend: ${personOf(friendUid, people).name}` : "";
    }
    default: {
      const lessonId = t.bookingId || t.lessonId;
      return lessonId ? `Lesson ${lessonId}` : "";
    }
  }
};

export const ledgerRowView = (t = {}, people = {}) => {
  const look = rowLook(t);
  const { pill, tone } = LOOKS[look];
  const row = txnRow(t);
  const amount = look === "topup" || look === "checkout" ? `£${money(t.amount)}` : look === "tutorPay" ? egp(t.amount) : row.amountLabel;
  const negative = look === "topup" || look === "checkout" || look === "tutorPay" ? false : row.negative;
  return {
    id: t.id,
    look,
    pill,
    tone,
    title: titleFor(look, t),
    sub: subFor(look, t, people),
    person: personOf(t.userId, people),
    personId: t.userId || null,
    date: dayTimeLabel(t.createdAt),
    amount,
    negative,
    muted: look === "checkout",
    lessonId: t.bookingId || t.lessonId || null,
    stripeId: t.paymentIntentId || null,
  };
};

const SOURCE = { chosen: "Chose to give", late: "Cancelled too late" };

export const donationRowView = (d = {}, people = {}) => ({
  id: d.id,
  date: dayTimeLabel(d.createdAt),
  student: personOf(d.studentId, people),
  tutor: personOf(d.teacherId, people),
  studentId: d.studentId || null,
  teacherId: d.teacherId || null,
  lessonId: d.lessonId || null,
  reason: d.reason || "—",
  source: SOURCE[d.source] || "—",
  fee: egp(d.amount),
});

export const isStripeId = (term) => /^pi_[A-Za-z0-9]+$/.test(String(term || "").trim());

// R9: RFC 4180 — quote a cell holding a comma, a quote or a line break, doubling its
// quotes; a text cell starting with = + - @, a tab or a return gets a leading ' so a
// spreadsheet reads it as text, never as a formula. Numbers go out as they are.
const csvCell = (v) => {
  if (typeof v === "number") return String(v);
  let s = v == null ? "" : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
// The byte-order mark makes Excel read UTF-8, so Arabic names survive.
export const csvText = (header, rows) =>
  `﻿${[header, ...rows].map((r) => r.map(csvCell).join(",")).join("\r\n")}\r\n`;

const csvDate = (value) => {
  const d = toDate(value);
  if (!d) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const LEDGER_HEADER = ["Date", "Type", "What", "Person", "Email", "Amount", "Unit", "Stripe id", "Lesson id", "Reason"];

export const ledgerCsv = (rows, people) =>
  csvText(
    LEDGER_HEADER,
    rows.map((t) => {
      const view = ledgerRowView(t, people);
      const look = view.look;
      const unit = look === "topup" || look === "checkout" ? "GBP" : look === "tutorPay" ? "EGP" : t.currency ? String(t.currency).toUpperCase() : "sessions";
      const amount = look === "topup" || look === "checkout" || look === "tutorPay" ? Number(t.amount) || 0 : view.negative ? -(Number(t.amount) || 0) : Number(t.amount) || 0;
      return [
        csvDate(t.createdAt),
        view.pill,
        view.title,
        view.person.name,
        people[t.userId]?.email || "",
        amount,
        unit,
        t.paymentIntentId || "",
        t.bookingId || t.lessonId || "",
        t.reason || "",
      ];
    })
  );

const DONATION_HEADER = ["Date", "Student", "Tutor", "Lesson id", "Reason", "Source", "Fee", "Unit"];

export const donationCsv = (rows, people) =>
  csvText(
    DONATION_HEADER,
    rows.map((d) => [
      csvDate(d.createdAt),
      personOf(d.studentId, people).name,
      personOf(d.teacherId, people).name,
      d.lessonId || "",
      d.reason || "",
      SOURCE[d.source] || "",
      Number(d.amount) || 0,
      "EGP",
    ])
  );

export const ymd = (d) => {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export const exportFileName = (tab, range, now) =>
  `transactions-${tab}-${ymd(range.from)}-to-${ymd(periodEnd(range, now))}.csv`;
